import { useEffect, useRef, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { num2 } from "@/lib/format";

const blank = () => ({ base: "", mult: 1 });
const filled = (r) => String(r.base ?? "") !== "";

export default function MultiplierBreakdownModal({ open, unit, initialRows, onSave, onClose, lineIndex, productName }) {
  const [rows, setRows] = useState([]);
  const firstRef = useRef(null);

  useEffect(() => {
    if (open) {
      setRows(initialRows?.length ? [...initialRows.map(r=>({...r})), blank()] : [blank(), blank(), blank()]);
      setTimeout(()=>firstRef.current?.focus(), 40);
    }
  }, [open, initialRows]);

  if (!open) return null;
  const sum = rows.reduce((s, r) => s + (parseFloat(r.base||0) * parseFloat(r.mult||0)), 0);

  const update = (i, k, v) => setRows(rs => {
    const next = rs.map((r,idx)=> idx===i ? { ...r, [k]: v } : r);
    if (i === next.length - 1 && filled(next[i])) next.push(blank());
    return next;
  });
  const removeRow = (i) => setRows(rs => rs.length > 1 ? rs.filter((_,idx)=>idx!==i) : rs);

  const done = () => {
    const clean = rows.filter(r => parseFloat(r.base||0) > 0).map(r => ({ base: parseFloat(r.base), mult: parseFloat(r.mult===""?1:r.mult) }));
    onSave(clean, Number(sum.toFixed(2)));
  };

  const focusEl = (id) => setTimeout(()=>document.querySelector(`[data-testid='${id}']`)?.focus(), 30);
  const onKey = (i, field, e) => {
    if (e.key === "Escape") { e.preventDefault(); done(); return; }
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (field === "base") focusEl(`mult-count-${i}`);
    else if (i < rows.length - 1) focusEl(`mult-base-${i+1}`);
    else done();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onClick={done}>
      <div className="bg-white rounded-lg border border-slate-300 shadow-xl max-w-2xl w-full p-5" onClick={(e)=>e.stopPropagation()} data-testid="multiplier-modal">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-lg font-semibold" style={{fontFamily:'Plus Jakarta Sans'}}>Quantity Breakdown</h3>
            <div className="text-xs text-slate-500 mt-0.5">Line #{(lineIndex??0)+1} · <b>{productName}</b> · Base Size × Multiplier — total is pushed to the line Qty</div>
          </div>
          <button onClick={done} className="text-slate-400 hover:text-slate-900" data-testid="mult-close-btn" title="Done"><X size={18}/></button>
        </div>

        <div className="mt-3 border border-slate-200 rounded-md overflow-hidden max-h-[60vh] overflow-y-auto">
          <table className="acct-table fit">
            <thead>
              <tr>
                <th style={{width: "34%"}}>Base Size ({unit})</th>
                <th className="text-center" style={{width: "8%"}}>X</th>
                <th style={{width: "26%"}}>Multiplier</th>
                <th className="num" style={{width: "24%"}}>Subtotal</th>
                <th style={{width: "8%"}}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r,i) => {
                const sub = parseFloat(r.base||0) * parseFloat(r.mult||0);
                return (
                  <tr key={i}>
                    <td><input ref={i===0?firstRef:null} data-testid={`mult-base-${i}`} className="cell-input num" type="number" step="0.01"
                      value={r.base} onChange={(e)=>update(i,"base",e.target.value)} onKeyDown={(e)=>onKey(i,"base",e)} placeholder="0.00"/></td>
                    <td className="text-center text-slate-400 mono">×</td>
                    <td><input data-testid={`mult-count-${i}`} className="cell-input num" type="number" step="1"
                      value={r.mult} onChange={(e)=>update(i,"mult",e.target.value)} onKeyDown={(e)=>onKey(i,"mult",e)} placeholder="1"/></td>
                    <td className="num mono" data-testid={`mult-subtotal-${i}`}>{num2(sub)}</td>
                    <td className="text-right"><button onClick={()=>removeRow(i)} className="p-1 text-slate-400 hover:text-rose-700" data-testid={`mult-remove-${i}`}><Trash2 size={14}/></button></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold">
                <td colSpan="3">Total Quantity ({unit})</td>
                <td className="num mono" data-testid="mult-total">{num2(sum)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">Rows auto-add as you type · Enter to move · Esc to finish</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-9 px-4 border border-slate-300 rounded-md text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50" data-testid="mult-cancel-btn">Cancel</button>
            <button onClick={done} className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs font-semibold uppercase tracking-wider" data-testid="mult-save-btn">Done · Push {num2(sum)} to Qty</button>
          </div>
        </div>
      </div>
    </div>
  );
}
